/**
 * ContextoForm — el formulario de "Contexto de marca" (paso 4 del wizard), COMPLETO.
 *
 * Por qué existe: el wizard mandaba 7 campos al backend pero sólo preguntaba 2 (tono y techo).
 * Los otros 5 —usaDescuentos, descuento, cuponBienvenida, canales, programaPuntos— iban
 * HARDCODEADOS (`descuento: "20%"`, `canales: "Todos"`…). Condicionan lo que se le ofrece a
 * un cliente real, así que los decide la marca, no una constante escondida en el código.
 *
 * Criterio visual: una pregunta por bloque, en el orden en que un dueño de local las piensa
 * ("cómo hablo" → "qué ofrezco" → "hasta dónde"). Lo que no aplica no se muestra: si no usás
 * descuentos, las preguntas sobre descuentos desaparecen en vez de quedar grises.
 */

import { useEffect, useState } from "react";
import { MessageCircle, Tag, ShieldCheck, Gift, Star, Send, Clock, Target } from "lucide-react";
import type { ContextReal } from "../types.real";
import { money } from "../utils/format";

/**
 * El contexto se pregunta en pasos, no en una pantalla larga: son tres decisiones distintas y
 * mezclarlas hace que el techo —la única que el sistema FUERZA— se lea como un campo más.
 *   tono   → cómo le hablás
 *   oferta → qué le podés dar, y hasta dónde (el techo es parte del sistema de descuentos)
 *   envio  → por dónde salen y en qué horario
 */
export type SeccionContexto = "tono" | "oferta" | "envio";

interface Props {
  value: ContextReal;
  onChange: (v: ContextReal) => void;
  /** qué parte renderizar. Sin esto, se muestran todas (una sola pantalla). */
  seccion?: SeccionContexto;
  /**
   * Los grupos de la campaña. Sirven para una sola cosa, pero importante: mostrar cuánta gente
   * recibe los puntos y cuántos puntos son EN TOTAL. Un campo que sólo pide "500" esconde que son
   * 500 × 84 personas; el error caro no es el número por cabeza, es el cero de más multiplicado.
   */
  grupos?: { id: string; nombre?: string; personas?: number; descuentoPct?: number; descuentoPropuesto?: number }[];
  /**
   * Ticket TÍPICO (mediana) de una compra de un dormido, del reporte. Ancla en pesos del techo:
   * deja leer "un 15% ≈ $X" y ofrecer el modo puja ($). 0/ausente = no se pudo medir → el techo
   * se muestra sólo en %, sin espejo en pesos (nunca se inventa una cifra).
   */
  ticketDormido?: number;
  /**
   * La regla de fatiga de la MARCA (29/10/2026, WhatsApp → Configuración → Envío): «como mucho `maximo` mensajes cada
   * `dias` días por persona, sumando WhatsApp oficial y por QR». Vale siempre; los días de esta campaña sólo la hacen
   * más estricta. Ausente (la cuenta todavía no cargó, o falló) = se explica sin los números.
   */
  reglaMarca?: { dias: number; maximo: number; contarMails?: boolean } | null;
}

/** Límites del techo: el slider y el input tienen que respetar los mismos. */
const TECHO_MIN = 0;
const TECHO_MAX = 50;

/** Presets de franja: cubren el 90% de los casos sin hacer pensar en horarios. */
const FRANJAS = [
  { k: "comercial", label: "Horario comercial", desc: "9 a 21, todos los días", v: { desde: "09:00", hasta: "21:00", dias: [] as number[] } },
  { k: "laboral", label: "Días de semana", desc: "9 a 19, de lunes a viernes", v: { desde: "09:00", hasta: "19:00", dias: [1, 2, 3, 4, 5] } },
  { k: "tarde", label: "Tarde y noche", desc: "14 a 22, todos los días", v: { desde: "14:00", hasta: "22:00", dias: [] as number[] } },
  { k: "siempre", label: "Sin restricción", desc: "Sale apenas apretás enviar", v: null },
];
const DIAS_CORTOS = ["D", "L", "M", "M", "J", "V", "S"];

/** Vigencia del cupón. "Sin vencimiento" es una opción legítima, pero explícita. */
const VIGENCIAS = [
  { label: "3 días", dias: 3 },
  { label: "1 semana", dias: 7 },
  { label: "15 días", dias: 15 },
  { label: "1 mes", dias: 30 },
  { label: "Sin vencimiento", dias: 0 },
];

/**
 * Cada cuánto, como mucho, se le puede escribir a la misma persona en ESTA campaña. Desde el 29/10/2026 vale siempre la
 * regla de la marca (oficial + QR): estos días sólo la hacen más estricta. 0 = sólo la de la marca (antes «sin límite»).
 */
const FATIGAS = [
  { label: "La de la marca", dias: 0, desc: "sin días de más" },
  { label: "1 semana", dias: 7, desc: "recomendado" },
  { label: "15 días", dias: 15, desc: "" },
  { label: "1 mes", dias: 30, desc: "conservador" },
];

const TONOS = [
  { k: "Formal", ej: "Notamos que hace un tiempo no nos visitás." },
  { k: "Amigable", ej: "Te extrañamos 🍣 Volvé cuando quieras." },
  { k: "Divertido", ej: "¡Sin vos no es lo mismo! 😢 Volvé ya." },
];
const DESCUENTOS = ["10%", "15%", "20%", "2x1"];
const CANALES = ["Todos", "Solo WhatsApp", "Solo Email"];

/** Switch accesible — un checkbox real debajo, para que el teclado y los lectores funcionen. */
function Switch({ on, onToggle, label, desc, icon }: { on: boolean; onToggle: (v: boolean) => void; label: string; desc: string; icon: React.ReactNode }) {
  return (
    <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 bg-white hover:border-slate-200 transition-colors cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-violet-500">
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${on ? "bg-violet-50 text-violet-600" : "bg-slate-50 text-slate-300"}`}>
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-800">{label}</span>
        <span className="block text-xs text-slate-400 mt-0.5 leading-relaxed">{desc}</span>
      </span>
      <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} className="sr-only" />
      <span className={`relative w-10 h-6 rounded-full flex-shrink-0 transition-colors ${on ? "bg-violet-600" : "bg-slate-200"}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${on ? "left-5" : "left-1"}`} />
      </span>
    </label>
  );
}

// `n` queda en el tipo (las llamadas siguen pasándolo) pero ya no se dibuja: la numeración 01/02
// tenía sentido cuando el contexto era UNA pantalla; ahora que son tres pasos separados, el "01"
// (tono) vive en otro paso y en "Qué ofrecés" quedaba un "02" huérfano. Sin número, los tres pasos
// del contexto usan el mismo encabezado que VozForm — mismo peso de título y sub que cumple AA.
function Bloque({ titulo, sub, children }: { n: number; titulo: string; sub: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <header>
        <h3 className="text-sm font-bold text-slate-900">{titulo}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
      </header>
      {children}
    </section>
  );
}

/**
 * Lo que dice el bloque de fatiga (29/10/2026): la regla de la marca vale siempre y suma WhatsApp oficial y por QR; los
 * días de esta campaña sólo la hacen más estricta (mira los de la marca o los suyos, el mayor). Sin la regla a mano
 * (la cuenta del oficial no cargó), lo dice sin los números.
 */
export function TextoFatiga({ fatigaDias, regla }: { fatigaDias: number; regla?: Props["reglaMarca"] }) {
  const clase = "text-[11px] text-slate-500 leading-relaxed";
  const propios = Math.max(0, Number(fatigaDias) || 0);
  const quema = " Tres mensajes en una semana hacen que el cliente se vaya — y que WhatsApp mire mal a la línea.";
  if (!regla) {
    return (
      <p className={clase}>
        Queda afuera quien ya recibió lo que permite la regla de tu marca, sumando WhatsApp oficial y por QR (se cambia en
        WhatsApp → Configuración → Envío){propios > 0 ? <>; esta campaña mira además los últimos <strong>{propios} días</strong></> : null}.{quema}
      </p>
    );
  }
  const k = Math.max(1, Math.floor(Number(regla.maximo) || 1));
  const deLaMarca = Math.max(0, Number(regla.dias) || 0);
  const dias = Math.max(deLaMarca, propios);
  if (dias <= 0) {
    return <p className={clase}>Tu marca no tiene un límite de mensajes por persona y esta campaña tampoco: una persona que entra en varias campañas las recibe todas. Es la forma más rápida de quemar la base.</p>;
  }
  const canales = regla.contarMails ? "WhatsApp oficial, por QR y los mails" : "WhatsApp oficial y por QR";
  const reglaMarca = deLaMarca > 0
    ? `La regla de tu marca es como mucho ${k} ${k === 1 ? "mensaje" : "mensajes"} cada ${deLaMarca} ${deLaMarca === 1 ? "día" : "días"} (WhatsApp → Configuración → Envío)`
    : "Tu marca no tiene un límite propio (WhatsApp → Configuración → Envío)";
  return (
    <p className={clase}>
      Queda afuera quien ya recibió <strong>{k === 1 ? "un mensaje" : `${k} mensajes`}</strong> en los últimos{" "}
      <strong>{dias} {dias === 1 ? "día" : "días"}</strong>, sumando {canales}. {reglaMarca}
      {propios > deLaMarca ? "; esta campaña pide más días." : "."}{quema}
    </p>
  );
}

/** Cuánto duran los puntos. Más largos que los de un cupón: un punto es un saldo, no una promo. */
const VIGENCIAS_PUNTOS = [
  { label: "1 mes", dias: 30 },
  { label: "3 meses", dias: 90 },
  { label: "6 meses", dias: 180 },
  { label: "1 año", dias: 365 },
  { label: "Sin vencimiento", dias: 0 },
];

/** Cantidades típicas. Escribir a mano sigue siendo posible: esto sólo evita empezar de cero. */
const PUNTOS_SUGERIDOS = [100, 250, 500, 1000];

export default function ContextoForm({ value, onChange, seccion, grupos = [], ticketDormido = 0, reglaMarca = null }: Props) {
  /**
   * El descuento MÁS PROFUNDO que arma el motor para esta campaña. Sale de los grupos reales (no de
   * una constante hardcodeada que se desincroniza si cambia la segmentación): de acá para arriba,
   * mover el techo no cambia nada, porque `Math.min(propuesto, techo)` ya topa en este número. El
   * espejo en pesos y el modo puja lo usan para no mostrar plata que ningún cliente va a recibir.
   */
  const maxPropuesto = grupos.length
    ? Math.max(0, ...grupos.map((g) => Math.max(Number(g.descuentoPropuesto) || 0, Number(g.descuentoPct) || 0)))
    : 20;
  const set = <K extends keyof ContextReal>(k: K, v: ContextReal[K]) => onChange({ ...value, [k]: v });
  // set de VARIAS claves a la vez. Con dos `set()` seguidos el segundo pisa al primero (los dos parten
  // del mismo `value` viejo); la puja necesita mover techoPct y pujaPorCliente juntos.
  const setMany = (patch: Partial<ContextReal>) => onChange({ ...value, ...patch });
  const tonoEj = TONOS.find((t) => t.k === value.tono)?.ej;
  const ver = (s: SeccionContexto) => !seccion || seccion === s;

  const cupon = value.cupon || {};
  const setCupon = <K extends keyof NonNullable<ContextReal["cupon"]>>(k: K, v: NonNullable<ContextReal["cupon"]>[K]) =>
    set("cupon", { ...cupon, [k]: v });
  const vigenciaDias = cupon.vigenciaDias || 0;
  // se muestra la fecha que VERÍA el cliente si se enviara hoy — el backend la recalcula al enviar
  const fechaVence = vigenciaDias > 0
    ? new Date(Date.now() + vigenciaDias * 86400000).toLocaleDateString("es-AR", { day: "numeric", month: "long" })
    : "";

  /* ── PUNTOS ── */
  const puntos = value.puntos || {};
  const setPuntos = <K extends keyof NonNullable<ContextReal["puntos"]>>(k: K, v: NonNullable<ContextReal["puntos"]>[K]) =>
    set("puntos", { ...puntos, [k]: v });
  const puntosPorPersona = Math.max(0, Math.floor(Number(puntos.porPersona) || 0));
  const nombrePuntos = (puntos.nombre || "").trim() || "puntos";
  /**
   * Quién recibe los puntos: los grupos que NO llevan descuento. Es la misma regla que aplica el
   * backend (el descuento gana), y decirla acá evita la sorpresa de configurar 500 puntos y ver
   * que a la mitad de la base le llegó un 20% off.
   *
   * Con `usaDescuentos` apagado no hay descuento en ningún grupo: los puntos van a todos.
   */
  const gruposConPuntos = grupos.filter((g) => !value.usaDescuentos || !(Number(g.descuentoPct) > 0));
  const personasConPuntos = gruposConPuntos.reduce((a, g) => a + (Number(g.personas) || 0), 0);
  const totalPuntos = puntosPorPersona * personasConPuntos;
  const vigPuntos = puntos.vigenciaDias || 0;
  const fechaVencePuntos = vigPuntos > 0
    ? new Date(Date.now() + vigPuntos * 86400000).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" })
    : "";

  const franja = value.franjaHoraria;
  const presetActivo = franja
    ? FRANJAS.find((f) => f.v && f.v.desde === franja.desde && f.v.hasta === franja.hasta && (f.v.dias || []).join() === (franja.dias || []).join())?.k || "custom"
    : "siempre";
  const toggleDia = (d: number) => {
    if (!franja) return;
    const dias = franja.dias || [];
    // Destildar el ÚLTIMO día dejaría la lista vacía, que en este contrato significa "todos los
    // días": el usuario apagaría el único día que quedaba y se le encenderían los siete. Se
    // ignora el clic — para volver a "todos" están los presets.
    if (dias.includes(d) && dias.length === 1) return;
    set("franjaHoraria", { ...franja, dias: dias.includes(d) ? dias.filter((x) => x !== d) : [...dias, d].sort() });
  };

  // El techo se puede escribir además de arrastrar: el slider va de a 1, pero tipear es la única
  // forma cómoda de poner un 17% exacto. Se mantiene texto propio mientras se edita para no
  // pelearle al usuario mientras borra (un value numérico controlado convierte "" en 0 y le
  // planta un cero en el campo apenas hace backspace).
  const [techoTxt, setTechoTxt] = useState(String(value.techoPct ?? 0));
  useEffect(() => { setTechoTxt(String(value.techoPct ?? 0)); }, [value.techoPct]);

  const clampTecho = (n: number) => Math.max(TECHO_MIN, Math.min(TECHO_MAX, Math.round(n)));
  /** Al salir del campo: si quedó vacío o inválido, se vuelve al último valor bueno. */
  const commitTecho = (raw: string) => {
    const n = Number(raw);
    if (!raw.trim() || Number.isNaN(n)) { setTechoTxt(String(value.techoPct ?? 0)); return; }
    const c = clampTecho(n);
    set("techoPct", c);
    setTechoTxt(String(c));
  };

  return (
    <div className="space-y-8">
      {/* 01 · Tono ─ se elige viendo cómo suena, no leyendo una etiqueta */}
      {ver("tono") && (
      <Bloque n={1} titulo="¿Cómo le hablás a tus clientes?" sub="Con este tono se escriben todos los mensajes.">
        <div className="flex gap-2 flex-wrap">
          {TONOS.map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => set("tono", t.k)}
              aria-pressed={value.tono === t.k}
              className={`px-4 h-10 rounded-xl text-sm font-semibold border transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                value.tono === t.k
                  ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"
              }`}
            >
              {t.k}
            </button>
          ))}
        </div>
        {tonoEj && (
          <div className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-slate-100 p-3.5">
            <MessageCircle size={15} className="text-slate-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 italic">"{tonoEj}"</p>
          </div>
        )}
      </Bloque>
      )}

      {/* 02 · Reglas comerciales ─ revelación progresiva: sin descuentos, no hay sub-preguntas */}
      {ver("oferta") && (
      <Bloque n={2} titulo="¿Qué le podés ofrecer?" sub="Lo que la marca está dispuesta a dar. Nada de esto se inventa solo.">
        <div className="space-y-2.5">
          <Switch
            on={value.usaDescuentos}
            // apagar los descuentos apaga también la puja: una puja sin descuentos fija un techo que
            // el backend fuerza a 0 → no saldría nada, y el switch quedaría prendido mintiendo
            onToggle={(v) => setMany(v ? { usaDescuentos: true } : { usaDescuentos: false, usaPuja: false })}
            label="Trabajo con descuentos"
            // «sólo beneficios y novedades» prometía algo que el mensaje no da: sin descuentos ni puntos, el
            // mensaje invita a volver sin ofrecer nada (el cierre `sinOferta` del backend, QA del 08/10)
            desc="Si lo apagás, los mensajes invitan a volver sin ofrecer descuentos. Si tenés programa de puntos, ofrecen los puntos."
            icon={<Tag size={16} />}
          />

          {value.usaDescuentos && (
            <div className="ml-3 pl-5 border-l-2 border-violet-100 space-y-2.5 animate-fade-in">
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-sm font-semibold text-slate-800">Tu descuento habitual</p>
                <p className="text-xs text-slate-400 mt-0.5 mb-3">El que solés ofrecer cuando querés que alguien vuelva.</p>
                <div className="flex gap-2 flex-wrap">
                  {DESCUENTOS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => set("descuento", d)}
                      aria-pressed={value.descuento === d}
                      className={`px-3.5 h-9 rounded-lg text-sm font-semibold border tabular-nums transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                        value.descuento === d
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* El techo va PEGADO al descuento habitual: son la misma decisión leída dos veces
                  ("suelo dar 20%" → "pero nunca más de 15%"), y el aviso de que uno supera al otro
                  queda justo debajo de los dos números que compara. */}
              <TechoDescuento value={value} set={set} techoTxt={techoTxt} setTechoTxt={setTechoTxt} commitTecho={commitTecho} ticketDormido={ticketDormido} maxPropuesto={maxPropuesto} />

              <Switch
                on={value.cuponBienvenida}
                onToggle={(v) => set("cuponBienvenida", v)}
                label="Doy cupón de bienvenida"
                desc="Para los que compraron una sola vez: la segunda compra define si se quedan."
                icon={<Gift size={16} />}
              />

              {/* CÓMO SE CANJEA. Sin esto el mensaje decía "20% off" y listo: el cliente llegaba
                  al mostrador y el mozo no tenía con qué validarlo. */}
              <div className="rounded-xl border border-slate-100 bg-white p-4 space-y-3.5">
                <div>
                  <p className="text-sm font-semibold text-slate-800">¿Cómo lo canjea?</p>
                  <p className="text-xs text-slate-400 mt-0.5">Esto va escrito en el mensaje que recibe el cliente.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Código del cupón</span>
                    <input
                      type="text"
                      value={cupon.codigo || ""}
                      onChange={(e) => setCupon("codigo", e.target.value.toUpperCase().slice(0, 20))}
                      placeholder="VUELVE20"
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono uppercase placeholder:font-sans placeholder:normal-case placeholder:text-slate-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                    />
                    <span className="block text-[11px] text-slate-400 mt-1">El que dice en la caja. Vacío = no se menciona ninguno.</span>
                  </label>

                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Compra mínima</span>
                    <div className="mt-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={cupon.montoMinimo ? String(cupon.montoMinimo) : ""}
                        onChange={(e) => setCupon("montoMinimo", Number(e.target.value.replace(/[^\d]/g, "").slice(0, 8)) || undefined)}
                        placeholder="sin mínimo"
                        className="w-full h-10 pl-7 pr-3 rounded-lg border border-slate-200 text-sm tabular-nums placeholder:text-slate-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                      />
                    </div>
                  </label>
                </div>

                <div>
                  <span className="text-xs font-semibold text-slate-600">¿Hasta cuándo vale?</span>
                  <div className="flex gap-2 flex-wrap mt-1.5">
                    {VIGENCIAS.map((v) => (
                      <button
                        key={v.label}
                        type="button"
                        onClick={() => setCupon("vigenciaDias", v.dias)}
                        aria-pressed={(cupon.vigenciaDias || 0) === v.dias}
                        className={`px-3.5 h-9 rounded-lg text-sm font-semibold border transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                          (cupon.vigenciaDias || 0) === v.dias
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {vigenciaDias > 0 ? (
                      <>Se cuenta desde que <strong>sale</strong> el mensaje: si se envía hoy, dice “válido hasta el <strong>{fechaVence}</strong>”.</>
                    ) : (
                      <>Sin vencimiento, el mensaje no promete ninguna fecha.</>
                    )}
                  </p>
                </div>

                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">¿Qué NO entra? <span className="font-normal text-slate-400">(opcional)</span></span>
                  <input
                    type="text"
                    value={cupon.exclusiones || ""}
                    onChange={(e) => setCupon("exclusiones", e.target.value.slice(0, 60))}
                    placeholder="no aplica a bebidas"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm placeholder:text-slate-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                  />
                </label>
              </div>
            </div>
          )}

          <Switch
            on={value.programaPuntos}
            onToggle={(v) => set("programaPuntos", v)}
            label="Tengo programa de puntos"
            desc="A quien no le toca descuento, le repartimos puntos: se dice cuántos y hasta cuándo valen."
            icon={<Star size={16} />}
          />

          {/* CUÁNTOS Y HASTA CUÁNDO. Sin estos dos números el switch no hacía nada: el mensaje
              decía "un beneficio VIP" —una frase que no le dice al cliente qué se lleva— y era
              justo lo que recibían los clientes más fieles. */}
          {value.programaPuntos && (
            <div className="ml-3 pl-5 border-l-2 border-violet-100 space-y-2.5 animate-fade-in">
              <div className="rounded-xl border border-slate-100 bg-white p-4 space-y-3.5">
                <div>
                  <p className="text-sm font-semibold text-slate-800">¿Cuántos {nombrePuntos} le das a cada uno?</p>
                  <p className="text-xs text-slate-400 mt-0.5">Esto va escrito en el mensaje: “{puntosPorPersona > 0 ? puntosPorPersona.toLocaleString("es-AR") : "500"} {nombrePuntos} para tu próximo pedido”.</p>
                </div>

                <div className="flex gap-2 flex-wrap items-center">
                  {PUNTOS_SUGERIDOS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPuntos("porPersona", n)}
                      aria-pressed={puntosPorPersona === n}
                      className={`px-3.5 h-9 rounded-lg text-sm font-semibold border tabular-nums transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                        puntosPorPersona === n
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {n.toLocaleString("es-AR")}
                    </button>
                  ))}
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label={`Otra cantidad de ${nombrePuntos} por persona`}
                    // vacío cuando el valor ya está en un preset: si no, el 500 se veía DOS veces
                    // (el botón marcado y el campo con "500") y parecían dos cantidades distintas
                    value={puntosPorPersona && !PUNTOS_SUGERIDOS.includes(puntosPorPersona) ? String(puntosPorPersona) : ""}
                    // el tope es el mismo que aplica el backend: acá se evita el susto, allá se
                    // evita el envío (el front no es una defensa)
                    onChange={(e) => setPuntos("porPersona", Math.min(100000, Number(e.target.value.replace(/[^\d]/g, "").slice(0, 6)) || 0) || undefined)}
                    placeholder="otro"
                    className="w-24 h-9 px-3 rounded-lg border border-slate-200 text-sm tabular-nums placeholder:text-slate-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                  />
                </div>

                {/* Switch prendido y cantidad en CERO: el operador cree que está repartiendo
                    puntos y el mensaje vuelve a decir "un beneficio VIP" — el mismo hueco que este
                    panel vino a tapar, ahora en silencio. No se elige un número por él (es plata
                    de la marca): se le avisa. */}
                {puntosPorPersona === 0 && (
                  <div className="rounded-lg p-3 text-xs leading-relaxed border bg-amber-50 border-amber-200 text-amber-800">
                    Todavía no elegiste cuántos {nombrePuntos} das. Así, el mensaje no los menciona:
                    {value.usaDescuentos && value.techoPct > 0
                      ? <> a esa gente le va a llegar “un beneficio VIP”, que no dice qué se lleva.</>
                      : <> como no trabajás con descuentos, el mensaje no le ofrece nada a nadie.</>}
                  </div>
                )}

                {/* LA CONSECUENCIA, no sólo el dato. Un campo que pide "500" esconde que son
                    500 × 84 personas: el error caro es el cero de más multiplicado. */}
                {puntosPorPersona > 0 && (
                  <div className={`rounded-lg p-3 text-xs leading-relaxed border ${totalPuntos > 500000 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-100 text-slate-600"}`}>
                    {personasConPuntos > 0 ? (
                      <>
                        Repartís <strong className="tabular-nums">{totalPuntos.toLocaleString("es-AR")} {nombrePuntos}</strong> en total
                        {" "}({puntosPorPersona.toLocaleString("es-AR")} × <strong className="tabular-nums">{personasConPuntos.toLocaleString("es-AR")}</strong> personas
                        {value.usaDescuentos && gruposConPuntos.length < grupos.length ? ", las que no llevan descuento" : ""}).
                        {totalPuntos > 500000 && <> ¿Es el número que querías?</>}
                      </>
                    ) : grupos.length > 0 ? (
                      <>Con los descuentos prendidos, todos los grupos llevan %: <strong>no le tocan {nombrePuntos} a nadie</strong>. Apagá “Trabajo con descuentos” o bajá el techo si querés repartir {nombrePuntos}.</>
                    ) : (
                      <>{puntosPorPersona.toLocaleString("es-AR")} {nombrePuntos} por persona.</>
                    )}
                  </div>
                )}

                <div>
                  <span className="text-xs font-semibold text-slate-600">¿Cuándo vencen?</span>
                  <div className="flex gap-2 flex-wrap mt-1.5">
                    {VIGENCIAS_PUNTOS.map((v) => (
                      <button
                        key={v.label}
                        type="button"
                        onClick={() => setPuntos("vigenciaDias", v.dias)}
                        aria-pressed={vigPuntos === v.dias}
                        className={`px-3.5 h-9 rounded-lg text-sm font-semibold border transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                          vigPuntos === v.dias
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {vigPuntos > 0 ? (
                      <>Se cuenta desde que <strong>sale</strong> el mensaje: si se envía hoy, dice “válidos hasta el <strong>{fechaVencePuntos}</strong>”. El vencimiento es lo que convierte un punto en un motivo para volver.</>
                    ) : (
                      <>Sin vencimiento, el mensaje no promete ninguna fecha — y el cliente lo archiva sin apuro.</>
                    )}
                  </p>
                </div>

                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">¿Cómo los llamás? <span className="font-normal text-slate-400">(opcional)</span></span>
                  <input
                    type="text"
                    value={puntos.nombre || ""}
                    onChange={(e) => setPuntos("nombre", e.target.value.slice(0, 20))}
                    placeholder="puntos"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm placeholder:text-slate-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                  />
                  <span className="block text-[11px] text-slate-400 mt-1">Si en tu local les decís “estrellas” o “gramos”, el mensaje usa esa palabra.</span>
                </label>
              </div>
            </div>
          )}

          {/* LA PUJA — la tercera opción, debajo de descuentos y puntos. Se ACEPTA con su switch y
              ahí se configura. Se puede prender SOLA (sin tocar descuentos ni puntos): como es una
              forma de dar descuento, al aceptarla se prende el descuento automáticamente —si no, la
              puja fijaría un techo que el backend igual forzaría a 0 y no saldría nada (falso éxito).
              Sólo pide un ticket de dormido medido: sin eso no hay con qué hacer la cuenta en pesos,
              y ofrecerla sería inventar plata. */}
          {ticketDormido > 0 && maxPropuesto > 0 && (
            <>
              <Switch
                on={!!value.usaPuja}
                onToggle={(v) => {
                  // apagar: sólo vuelve al techo en % (no borra el $ tipeado ni apaga el descuento)
                  if (!v) { setMany({ usaPuja: false }); return; }
                  // aceptar: prende el descuento (para que la puja realmente produzca algo) y arranca
                  // desde el techo actual —o 15% si estaba en 0— así el $ y el % coinciden de entrada
                  const techo = value.techoPct > 0 ? value.techoPct : 15;
                  setMany({ usaPuja: true, usaDescuentos: true, techoPct: techo, pujaPorCliente: Math.round((techo / 100) * ticketDormido) || undefined });
                }}
                label="Fijar el techo como una puja"
                desc="Das un descuento, pero lo dimensionás en $: cuánto te bancás que te cueste por cliente. Como en Meta Ads."
                icon={<Target size={16} />}
              />
              {value.usaPuja && <PujaTecho value={value} setMany={setMany} ticketDormido={ticketDormido} maxPropuesto={maxPropuesto} />}
            </>
          )}
        </div>
      </Bloque>
      )}

      {/* 03 · Canales */}
      {ver("envio") && (
      <Bloque n={3} titulo="¿Por dónde salen los mensajes?" sub="Si un cliente no tiene WhatsApp, igual le llega por mail.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {CANALES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => set("canales", c)}
              aria-pressed={value.canales === c}
              className={`h-11 rounded-xl text-sm font-semibold border transition-all inline-flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-violet-400 ${
                value.canales === c
                  ? "bg-violet-50 text-violet-700 border-violet-300"
                  : "bg-white text-slate-600 border-slate-200 hover:border-violet-200"
              }`}
            >
              <Send size={14} className={value.canales === c ? "text-violet-500" : "text-slate-300"} />
              {c}
            </button>
          ))}
        </div>
      </Bloque>
      )}

      {/* 03b · Franja horaria ─ el envío la respeta de verdad: lo que cae afuera no sale.
          Decía «queda pendiente y sigue cuando vuelve a ser hora», y no es así: un envío cortado por la franja no se
          reanuda solo (CLAUDE.md §8, QA del 08/10). El detalle y el modal de envío ya lo decían; acá faltaba. */}
      {ver("envio") && (
      <Bloque n={4} titulo="¿En qué horario pueden salir?" sub="Fuera de esta franja no se manda nada, y lo que quede sin salir no sigue solo: cuando vuelva a ser hora, hay que reanudar el envío desde la campaña.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {FRANJAS.map((f) => {
            const activo = presetActivo === f.k;
            return (
              <button
                key={f.k}
                type="button"
                onClick={() => set("franjaHoraria", f.v ? { ...f.v } : undefined)}
                aria-pressed={activo}
                className={`text-left p-3.5 rounded-xl border transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                  activo ? "bg-violet-50 border-violet-300" : "bg-white border-slate-200 hover:border-violet-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Clock size={14} className={activo ? "text-violet-500" : "text-slate-300"} />
                  <span className={`text-sm font-semibold ${activo ? "text-violet-800" : "text-slate-700"}`}>{f.label}</span>
                </span>
                <span className="block text-xs text-slate-400 mt-0.5 ml-6">{f.desc}</span>
              </button>
            );
          })}
        </div>

        {/* ajuste fino — sólo si eligió una franja */}
        {franja && (
          <div className="rounded-xl border border-slate-100 bg-white p-4 space-y-4 animate-fade-in">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-slate-600">Desde</span>
              <input
                type="time"
                value={franja.desde}
                onChange={(e) => set("franjaHoraria", { ...franja, desde: e.target.value })}
                aria-label="Hora de inicio de la franja"
                className="h-9 px-2.5 rounded-lg border border-slate-200 text-sm tabular-nums focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
              />
              <span className="text-xs font-semibold text-slate-600">hasta</span>
              <input
                type="time"
                value={franja.hasta}
                onChange={(e) => set("franjaHoraria", { ...franja, hasta: e.target.value })}
                aria-label="Hora de fin de la franja"
                className="h-9 px-2.5 rounded-lg border border-slate-200 text-sm tabular-nums focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
              />
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Días</p>
              <div className="flex gap-1.5">
                {DIAS_CORTOS.map((d, i) => {
                  const todos = !franja.dias || franja.dias.length === 0;
                  const on = todos || franja.dias!.includes(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        // "todos" es lista vacía: al tocar un día por primera vez, arrancamos
                        // desde la semana completa y le sacamos ese, que es lo que espera el usuario
                        if (todos) set("franjaHoraria", { ...franja, dias: [0, 1, 2, 3, 4, 5, 6].filter((x) => x !== i) });
                        else toggleDia(i);
                      }}
                      aria-pressed={on}
                      aria-label={["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"][i]}
                      className={`w-9 h-9 rounded-lg text-xs font-bold border transition-colors focus-visible:ring-2 focus-visible:ring-violet-400 ${
                        on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>

            {franja.desde > franja.hasta && (
              <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                La franja cruza la medianoche: se manda de <strong>{franja.desde}</strong> a <strong>{franja.hasta}</strong> del día siguiente.
              </p>
            )}
            {/* Lista de días VACÍA = todos los días. Es el contrato del backend
                (wa-utils.ts:96 sólo filtra si `dias.length > 0`) y es lo que dibujan los chips
                de arriba: con la lista vacía se muestran los 7 encendidos.
                Antes acá había un aviso que decía "sin ningún día seleccionado" para ese mismo
                estado: el preset "Horario comercial · todos los días" prendía los 7 chips y en
                el mismo panel avisaba que no había ninguno, con el botón Siguiente bloqueado. */}
            {franja.dias && franja.dias.length > 0 && franja.dias.length < 7 && (
              <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                Sólo se manda los días marcados. El resto queda esperando al próximo día habilitado.
              </p>
            )}
          </div>
        )}

        {!franja && (
          <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            Sin franja, los mensajes salen apenas apretás enviar — incluso de madrugada. Un mensaje a las 3am molesta al
            cliente y expone la línea.
          </p>
        )}
      </Bloque>
      )}

      {/* Fatiga ─ hermano del no-contactar: aquél protege al que se quejó, éste al que todavía no */}
      {ver("envio") && (
      <Bloque n={5} titulo="¿Cada cuánto, como mucho?" sub="Vale siempre la regla de tu marca, que suma WhatsApp oficial y por QR. Acá podés pedir más días para esta campaña.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FATIGAS.map((f) => {
            const activo = (value.fatigaDias || 0) === f.dias;
            return (
              <button
                key={f.label}
                type="button"
                onClick={() => set("fatigaDias", f.dias)}
                aria-pressed={activo}
                className={`p-3 rounded-xl border text-left transition-all focus-visible:ring-2 focus-visible:ring-violet-400 ${
                  activo ? "bg-violet-50 border-violet-300" : "bg-white border-slate-200 hover:border-violet-200"
                }`}
              >
                <span className={`block text-sm font-semibold ${activo ? "text-violet-800" : "text-slate-700"}`}>{f.label}</span>
                {f.desc && <span className="block text-[11px] text-slate-400 mt-0.5">{f.desc}</span>}
              </button>
            );
          })}
        </div>
        <TextoFatiga fatigaDias={value.fatigaDias || 0} regla={reglaMarca} />
      </Bloque>
      )}

    </div>
  );
}

/**
 * EL TECHO — el límite duro del descuento. Se ve distinto que el resto porque es la única perilla
 * que el sistema FUERZA (no es una sugerencia: `generarMensajes` hace `Math.min(propuesto, techo)`).
 *
 * Vive DENTRO de "Trabajo con descuentos" y no como un bloque aparte ("03 · ¿Hasta dónde podemos
 * llegar?") por dos motivos. El primero es que es literalmente un tema de descuentos: preguntar
 * "hasta dónde" separado de "qué ofrecés" parte en dos una sola decisión. El segundo es más duro:
 * con el switch apagado el techo vale 0 server-side, así que ese bloque mostraba un slider que ya
 * no cambiaba nada — un control muerto, que es peor que no tener control.
 */
/** El techo que REALMENTE muerde: nada arriba de `maxPropuesto` cambia lo que sale (Math.min topa). */
const techoQueMuerde = (techoPct: number, maxPropuesto: number) => Math.min(techoPct, maxPropuesto);

/**
 * La consecuencia en PESOS del techo, sobre una compra típica de un dormido. "por canje", NUNCA
 * "hasta": el guardrail topa el %, no el peso — sobre una compra más grande el descuento absoluto es
 * proporcionalmente mayor, así que no existe un techo en $ que prometer. Se calcula sobre el techo
 * que muerde (no el crudo) para no mostrar plata que ningún cliente va a recibir.
 */
function EspejoPesos({ techoEfectivo, ticketDormido }: { techoEfectivo: number; ticketDormido: number }) {
  const costo = Math.round((techoEfectivo / 100) * ticketDormido);
  if (costo <= 0) return null;
  return (
    <p className="text-[11px] text-slate-600 bg-white/70 border border-violet-100 rounded-lg px-3 py-2 mt-3 leading-relaxed">
      Un <strong>{techoEfectivo}%</strong> sobre una compra típica de un dormido (~{money(ticketDormido)}) son
      ≈ <strong className="text-violet-700">{money(costo)}</strong> de descuento <strong>por canje</strong>.
      <span className="text-slate-400"> En una compra más grande, proporcionalmente más — el tope es el %, no el peso.</span>
    </p>
  );
}

/** Avisos del techo que valen en los dos modos (manual y puja): el habitual que lo supera, y el 0%. */
function AvisosTecho({ value }: { value: ContextReal }) {
  return (
    <>
      {value.usaDescuentos && Number(String(value.descuento).replace(/\D/g, "")) > value.techoPct && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
          Tu descuento habitual ({value.descuento}) supera el techo: se va a ofrecer <strong>{value.techoPct}%</strong>.
        </p>
      )}
      {value.techoPct === 0 && (
        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-3">
          Con techo en 0% no se ofrece ningún descuento: los mensajes invitan a volver sin ofrecer nada (si tenés programa de puntos, ofrecen los puntos).
        </p>
      )}
    </>
  );
}

/**
 * EL TECHO en la sección de descuentos. Editable en % — el número ES el control.
 *
 * Cuando la PUJA está activa (bloque de más abajo), el techo NO se edita acá: lo fija la puja en
 * pesos y este control pasa a ser un resumen de sólo-lectura. Así hay una sola perilla del techo por
 * vez, nunca dos que se pisan.
 */
function TechoDescuento({ value, set, techoTxt, setTechoTxt, commitTecho, ticketDormido, maxPropuesto }: {
  value: ContextReal;
  set: <K extends keyof ContextReal>(k: K, v: ContextReal[K]) => void;
  techoTxt: string;
  setTechoTxt: (v: string) => void;
  commitTecho: (raw: string) => void;
  ticketDormido: number;
  maxPropuesto: number;
}) {
  const hayTicket = ticketDormido > 0 && maxPropuesto > 0;
  const pujaActiva = !!value.usaPuja && hayTicket;
  const techoEfectivo = techoQueMuerde(value.techoPct, maxPropuesto);

  return (
    <div className="rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50/70 to-white p-5">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-violet-600" />
        <p className="text-sm font-bold text-slate-800">Techo de descuento</p>
      </div>

      {pujaActiva ? (
        // ── read-only: lo está fijando la puja de más abajo ──
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs text-slate-500">
            Lo estás fijando con la <strong>puja</strong>, más abajo <span aria-hidden="true">↓</span>
          </p>
          <span className="text-4xl font-black text-violet-700 tabular-nums leading-none">{techoEfectivo}%</span>
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between gap-4 mb-1">
            <p className="text-xs text-slate-500 max-w-[62%]">
              Si algún mensaje ofrece más que esto, se <strong>recorta</strong>. No es una sugerencia.
            </p>
            {/* editable: el número ES el control, no una etiqueta del slider */}
            <label className="flex items-baseline gap-0.5 rounded-lg px-1.5 -mr-1.5 transition-colors hover:bg-violet-100/50 focus-within:bg-white focus-within:ring-2 focus-within:ring-violet-400 cursor-text">
              <span className="sr-only">Techo de descuento en porcentaje</span>
              <input
                type="text"
                inputMode="numeric"
                value={techoTxt}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d]/g, "").slice(0, 2);
                  setTechoTxt(v);
                  const n = Number(v);
                  if (v && !Number.isNaN(n) && n >= TECHO_MIN && n <= TECHO_MAX) set("techoPct", n);
                }}
                onBlur={(e) => commitTecho(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="Techo de descuento en porcentaje"
                className="w-[2.2ch] bg-transparent text-4xl font-black text-violet-700 tabular-nums leading-none text-right outline-none"
              />
              <span className="text-4xl font-black text-violet-700 leading-none" aria-hidden="true">%</span>
            </label>
          </div>
          <input
            type="range"
            min={TECHO_MIN}
            max={TECHO_MAX}
            step={1}
            value={value.techoPct}
            onChange={(e) => set("techoPct", Number(e.target.value))}
            aria-label="Techo de descuento en porcentaje (deslizador)"
            className="w-full accent-violet-600 cursor-pointer mt-3"
          />
          <div className="flex justify-between text-[11px] text-slate-400 tabular-nums mt-1.5">
            <span>0%</span><span>25%</span><span>50%</span>
          </div>
        </>
      )}

      {/* la consecuencia en pesos: honesta en los dos modos (con o sin puja) */}
      {hayTicket && value.usaDescuentos && <EspejoPesos techoEfectivo={techoEfectivo} ticketDormido={ticketDormido} />}
      <AvisosTecho value={value} />
    </div>
  );
}

/**
 * LA PUJA — el techo pensado como en Meta Ads: en vez de un %, el dueño dice cuánto se banca que le
 * cueste el descuento por cliente que lo usa, y el sistema deriva el `techoPct`.
 *
 * Es una OPCIÓN que se acepta (un switch, como descuentos y puntos), no un modo escondido. Vive
 * abajo de las dos porque configura CÓMO se fija el techo de los descuentos — no es un beneficio más.
 *
 * Honestidad (lo que el análisis dejó claro): NO se puede prometer un tope en $ (el guardrail topa el
 * %, no el peso). Así que la puja se traduce a un % —lo único que el sistema controla de verdad— y
 * jamás dice "hasta $X". Sólo aparece si hay un ticket de dormido medido: sin eso, no hay con qué
 * hacer la cuenta y ofrecerla sería inventar plata.
 */
function PujaTecho({ value, setMany, ticketDormido, maxPropuesto }: {
  value: ContextReal;
  setMany: (patch: Partial<ContextReal>) => void;
  ticketDormido: number;
  maxPropuesto: number;
}) {
  const techoEfectivo = techoQueMuerde(value.techoPct, maxPropuesto);
  const pujaNoVinculante = value.techoPct > maxPropuesto;
  // el $ EXACTO que puso el dueño (no el redondeado a % entero): si tipeó 5000, ve 5000, aunque el
  // techo que muerde sea 11%. Fallback al derivado del techo por si se abre sin haber tipeado.
  const pujaGuardada = value.pujaPorCliente ?? Math.round((value.techoPct / 100) * ticketDormido);

  const [pujaTxt, setPujaTxt] = useState(String(pujaGuardada || ""));
  // Se re-sincroniza SÓLO con cambios EXTERNOS del $ guardado (reabrir una campaña), no con lo que se
  // tipea acá: si escuchara cada tecla, "5000" saltaría al redondeo de % ($4.939) mientras se escribe.
  useEffect(() => { setPujaTxt(value.pujaPorCliente ? String(value.pujaPorCliente) : ""); }, [value.pujaPorCliente]);

  /**
   * $ → %: guarda el $ tal cual y deriva UN escalar `techoPct` (jamás por grupo). Si se derivara por
   * grupo, send() —que revalida el techo comparando un escalar contra el % de cada grupo— no podría
   * revalidar sin recomputar el ticket de cada grupo al enviar, y ese candado server-side se abriría.
   */
  const commitPuja = (raw: string) => {
    const pesos = Number(String(raw).replace(/[^\d]/g, ""));
    if (!pesos) return;
    const techoPct = Math.max(TECHO_MIN, Math.min(TECHO_MAX, Math.round((pesos / ticketDormido) * 100)));
    setMany({ pujaPorCliente: pesos, techoPct });
  };

  return (
    <div className="ml-3 pl-5 border-l-2 border-violet-100 space-y-2.5 animate-fade-in">
      <div className="rounded-xl border border-slate-100 bg-white p-4">
        <p className="text-sm font-semibold text-slate-800">¿Cuánto te bancás que te cueste?</p>
        <p className="text-xs text-slate-400 mt-0.5 mb-3">
          Por cada cliente que canjea el descuento. Lo convertimos en el techo de %, que es lo que el sistema controla.
        </p>
        <label className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 h-14 focus-within:ring-2 focus-within:ring-violet-400">
          <span className="text-2xl font-black text-slate-300" aria-hidden="true">$</span>
          <input
            type="text"
            inputMode="numeric"
            value={pujaTxt}
            onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, "").slice(0, 7); setPujaTxt(v); commitPuja(v); }}
            onBlur={(e) => commitPuja(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Cuánto te bancás que cueste el descuento por cliente, en pesos"
            placeholder="0"
            className="w-full bg-transparent text-2xl font-black text-slate-800 tabular-nums outline-none placeholder:text-slate-300"
          />
          <span className="text-[11px] text-slate-400 whitespace-nowrap">por canje</span>
        </label>
        <p className="text-[11px] text-slate-500 mt-2">
          Eso es un techo del <strong className="text-violet-700">{techoEfectivo}%</strong>.
          {pujaNoVinculante && (
            <> Tu puja da {value.techoPct}%, pero el descuento más profundo que arma el sistema es {maxPropuesto}%, así que no va a costar más que eso.</>
          )}
        </p>
      </div>
    </div>
  );
}
